import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { ArrowRight, ChevronLeft, Calendar, Heart, Palette, GraduationCap, BookOpen, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';
import { AVATARS, AvatarDisplay } from '@/lib/avatars';

const INTEREST_OPTIONS = [
  'Science', 'Math', 'History', 'Geography', 'Space', 'Animals',
  'Technology', 'Art', 'Music', 'Sports', 'Reading', 'Cooking',
  'Environment', 'Mythology', 'Coding', 'Puzzles'
];

const HOBBY_OPTIONS = [
  'Cricket', 'Drawing', 'Singing', 'Dancing', 'Swimming',
  'Cycling', 'Chess', 'Gardening', 'Photography', 'Writing',
  'Skating', 'Badminton', 'Painting', 'Crafts', 'Football', 'Yoga'
];

const BOARD_OPTIONS = [
  { id: 'CBSE', label: 'CBSE' },
  { id: 'ICSE', label: 'ICSE' },
  { id: 'State Board', label: 'State Board' },
  { id: 'International', label: 'International (IB/Cambridge)' },
  { id: 'Other', label: 'Other' },
];

const CLASS_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  id: `Class ${i + 1}`,
  label: `Class ${i + 1}`,
}));

export default function StudentOnboarding() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = location.state?.user;
  const [step, setStep] = useState('profile');
  const [isCreating, setIsCreating] = useState(false);
  const [data, setData] = useState({
    name: user?.name || '',
    date_of_birth: '',
    avatar: '',
    gender: '',
    board: '',
    grade: '',
    language_preference: 'auto',
    interests: [],
    hobbies: [],
  });

  const toggleItem = (field, item) => {
    setData(prev => {
      const list = prev[field];
      return {
        ...prev,
        [field]: list.includes(item) ? list.filter(i => i !== item) : [...list, item]
      };
    });
  };

  const canProceed = data.name && data.date_of_birth && data.grade && data.board;

  const handleSubmit = async () => {
    setIsCreating(true);
    try {
      const res = await api.post('/auth/student-setup', data);
      const childId = res.data.child_id;
      toast.success("You're all set! Let's start learning!");
      navigate(`/child/${childId}/home`, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Something went wrong');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 via-white to-orange-50">
      <div className="max-w-lg mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-orange-400 to-pink-500 flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Welcome to Yukti!</h1>
          <p className="text-gray-500 mt-1">Tell us about yourself so we can personalise your learning</p>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className={`h-1.5 w-16 rounded-full ${step === 'profile' ? 'bg-orange-500' : 'bg-orange-200'}`} />
          <div className={`h-1.5 w-16 rounded-full ${step === 'interests' ? 'bg-orange-500' : 'bg-orange-200'}`} />
        </div>

        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          {step === 'profile' && (
            <div className="space-y-5">
              {/* Name */}
              <div>
                <Label className="text-sm font-semibold text-gray-700">Your Name</Label>
                <Input
                  data-testid="student-name-input"
                  value={data.name}
                  onChange={e => setData(p => ({ ...p, name: e.target.value }))}
                  placeholder="What should Yukti call you?"
                  className="mt-1"
                />
              </div>

              {/* DOB */}
              <div>
                <Label className="text-sm font-semibold text-gray-700">
                  <Calendar className="w-3.5 h-3.5 inline mr-1" />
                  Date of Birth <span className="text-red-500">*</span>
                </Label>
                <Input
                  data-testid="student-dob-input"
                  type="date"
                  value={data.date_of_birth}
                  onChange={e => setData(p => ({ ...p, date_of_birth: e.target.value }))}
                  className="mt-1"
                />
              </div>

              {/* Grade */}
              <div>
                <Label className="text-sm font-semibold text-gray-700">
                  <GraduationCap className="w-3.5 h-3.5 inline mr-1" />
                  Class <span className="text-red-500">*</span>
                </Label>
                <Select value={data.grade} onValueChange={v => setData(p => ({ ...p, grade: v }))}>
                  <SelectTrigger data-testid="student-grade-select" className="mt-1">
                    <SelectValue placeholder="Select your class" />
                  </SelectTrigger>
                  <SelectContent>
                    {CLASS_OPTIONS.map(o => (
                      <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Board */}
              <div>
                <Label className="text-sm font-semibold text-gray-700">
                  <BookOpen className="w-3.5 h-3.5 inline mr-1" />
                  Board <span className="text-red-500">*</span>
                </Label>
                <Select value={data.board} onValueChange={v => setData(p => ({ ...p, board: v }))}>
                  <SelectTrigger data-testid="student-board-select" className="mt-1">
                    <SelectValue placeholder="Select your board" />
                  </SelectTrigger>
                  <SelectContent>
                    {BOARD_OPTIONS.map(o => (
                      <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Gender */}
              <div>
                <Label className="text-sm font-semibold text-gray-700">Gender</Label>
                <div className="flex gap-2 mt-1">
                  {['Boy', 'Girl', 'Other'].map(g => (
                    <button
                      key={g}
                      data-testid={`student-gender-${g.toLowerCase()}`}
                      onClick={() => setData(p => ({ ...p, gender: g }))}
                      className={`flex-1 py-2.5 rounded-xl text-sm font-medium border-2 transition-all ${
                        data.gender === g
                          ? 'border-orange-500 bg-orange-50 text-orange-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>

              {/* Avatar */}
              <div>
                <Label className="text-sm font-semibold text-gray-700">
                  <Palette className="w-3.5 h-3.5 inline mr-1" />
                  Pick your avatar
                </Label>
                <div className="grid grid-cols-6 gap-2 mt-2">
                  {AVATARS.map(a => (
                    <button
                      key={a.id}
                      data-testid={`student-avatar-${a.id}`}
                      onClick={() => setData(p => ({ ...p, avatar: a.id }))}
                      className={`w-full aspect-square rounded-xl flex items-center justify-center transition-all ${
                        data.avatar === a.id
                          ? 'ring-2 ring-orange-500 ring-offset-2 scale-110'
                          : 'hover:scale-105'
                      }`}
                    >
                      <AvatarDisplay avatarId={a.id} size="sm" />
                    </button>
                  ))}
                </div>
              </div>

              <Button
                data-testid="student-next-btn"
                onClick={() => setStep('interests')}
                disabled={!canProceed}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white rounded-xl py-6 text-base font-semibold"
              >
                Next <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {step === 'interests' && (
            <div className="space-y-6">
              {/* Interests */}
              <div>
                <Label className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                  <Heart className="w-3.5 h-3.5 text-pink-500" />
                  What are you interested in?
                </Label>
                <p className="text-xs text-gray-400 mb-3">Pick as many as you like</p>
                <div className="flex flex-wrap gap-2">
                  {INTEREST_OPTIONS.map(item => (
                    <button
                      key={item}
                      data-testid={`student-interest-${item.toLowerCase()}`}
                      onClick={() => toggleItem('interests', item)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                        data.interests.includes(item)
                          ? 'bg-orange-100 border-orange-400 text-orange-700'
                          : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </div>

              {/* Hobbies */}
              <div>
                <Label className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                  <Palette className="w-3.5 h-3.5 text-purple-500" />
                  What do you enjoy doing?
                </Label>
                <p className="text-xs text-gray-400 mb-3">Pick your hobbies</p>
                <div className="flex flex-wrap gap-2">
                  {HOBBY_OPTIONS.map(item => (
                    <button
                      key={item}
                      data-testid={`student-hobby-${item.toLowerCase()}`}
                      onClick={() => toggleItem('hobbies', item)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                        data.hobbies.includes(item)
                          ? 'bg-purple-100 border-purple-400 text-purple-700'
                          : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setStep('profile')}
                  className="flex-1 rounded-xl py-6"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                <Button
                  data-testid="student-submit-btn"
                  onClick={handleSubmit}
                  disabled={isCreating}
                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white rounded-xl py-6 text-base font-semibold"
                >
                  {isCreating ? 'Setting up...' : "Let's Go!"}
                </Button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
